import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RuvixxDate from "components/RuvixxDate";

import { ApiKeysTableActions } from "./ApiKeysTableActions";

const handleCopyAccessId = async accessId => {
  try {
    await navigator.clipboard.writeText(accessId);
  } catch (err) {
    console.error("Failed to copy access id: ", err);
  }
};

const maskAccessId = (accessId, prefixLen = 4, suffixLen = 4) => {
  if (!accessId) return "";
  if (accessId.length <= prefixLen + suffixLen) return accessId;
  return `${accessId.slice(0, prefixLen)}…${accessId.slice(-suffixLen)}`;
};

const truncate = (text, max = 80) => {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export const getApiKeysTableColumns = ({ onRotate, onRevoke }) => {
  return [
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => <p className="tw:font-bold">{row.original?.id}</p>,
    },
    {
      header: "Service",
      id: "Service",
      accessorKey: "service_name",
      size: 140,
    },
    {
      header: "Access ID",
      id: "Access ID",
      accessorKey: "access_id",
      enableSorting: false,
      size: 140,
      cell: ({ row }) => {
        const accessId = row.original?.access_id;
        if (!accessId) return null;
        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="xs"
                  className="tw:font-mono"
                  onClick={() => handleCopyAccessId(accessId)}
                >
                  {maskAccessId(accessId)}
                </Button>
              }
            />
            <TooltipContent>{`${accessId} — click to copy`}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      header: "Scopes",
      id: "Scopes",
      accessorKey: "scopes",
      enableSorting: false,
      size: 180,
      cell: ({ row }) => {
        const scopes = row.original?.scopes || [];
        return (
          <div className="tw:flex tw:flex-wrap tw:gap-1">
            {scopes.map(scope => (
              <Badge key={scope} variant="secondary">
                {scope}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      header: "Description",
      id: "Description",
      accessorKey: "description",
      enableSorting: false,
      cell: ({ row }) => <div>{truncate(row.original?.description, 80)}</div>,
    },
    {
      header: "Last Used",
      id: "Last Used",
      accessorKey: "last_used_at",
      size: 140,
      cell: ({ row }) => {
        const date = row.original?.last_used_at;
        return date ? <RuvixxDate date={date} /> : <span>—</span>;
      },
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "revoked_at",
      size: 100,
      cell: ({ row }) => {
        const isRevoked = !!row.original?.revoked_at;
        return (
          <Badge variant={isRevoked ? "destructive" : "success"}>
            {isRevoked ? "Revoked" : "Active"}
          </Badge>
        );
      },
    },
    {
      header: "Created",
      id: "Created",
      accessorKey: "created_at",
      size: 140,
      cell: ({ row }) => {
        const date = row.original?.created_at;
        return date ? <RuvixxDate date={date} /> : null;
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: { isDisplay: true },
      cell: ({ row }) => (
        <ApiKeysTableActions
          original={row.original}
          onRotate={onRotate}
          onRevoke={onRevoke}
        />
      ),
    },
  ];
};

export const getApiKeysTableFilters = () => [
  {
    key: "service_name",
    title: "Service",
    type: "input",
  },
  {
    key: "status",
    title: "Status",
    type: "select",
    data: [
      { id: "active", name: "Active" },
      { id: "revoked", name: "Revoked" },
      { id: "all", name: "All" },
    ],
  },
];
